/* eslint-disable react/require-default-props */
import {
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { useState } from 'react';

import { DocumentStatus } from '../../../../../../api/enumerations';
import {
  convertNoticeCompanyType,
  NoticeCompanyType,
} from '../../../../../../api/theHive/notices/enumerations';
import {
  DocumentRow,
  NoticeDocumentsData,
} from '../../../../../../api/theHive/notices/types';
import { Constants } from '../../../../../../constants/hiveNotices';
import {
  IconArrowForwardIosMS,
  IconAttachFileMS,
  IconDescriptionMS,
} from '../../../../../../constants/icons';
import { formatDate } from '../../../../../../helpers';
import useGeneral from '../../../../../../hooks/useGeneral';
import {
  TableCellHeader,
  StyledCell,
  StyledRow,
  ButtonsWrapper,
  StatusCell,
  StatusChip,
  StatusChipChevron,
  OutlinedButton,
  DownloadButton,
  ActionButton,
  DocumentNameButton,
  StyledTooltip,
  StyledTable,
} from '../styles';
import {
  getDocumentRowKey,
  getDocumentRowName,
  getDocumentRowStatusLabel,
  getDocumentRowStatusStyle,
  isMissingRequirement,
  stripHtml,
} from '../utils';
import { ApproveMenu } from './ApproveMenu';
import { CancelDocumentDialog } from './CancelDocumentDialog';
import { DocumentHistoryDialog } from './DocumentHistoryDialog';
import { RejectDialog } from './RejectDialog';
import { RejectReasonDialog } from './RejectionReason';

interface DocumentsTableProps {
  name: string;
  companyType?: NoticeCompanyType | null;
  docList: DocumentRow[];
  emptyMessage?: string;
  docLoading: boolean;
  handleReplace: (docId: number) => Promise<void>;
  handleApprove: (docId?: number) => Promise<void>;
  handleApproveWithReservation: (
    reservationNote: string,
    docId: number
  ) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
  handleCancel?: (docId: number) => Promise<void>;
  disabled: boolean;
  hasOpenChangeRequest?: boolean;
  changeRequestDocIds?: Set<number>;
  hideTitle?: boolean;
}

export default function DocumentsTable({
  name,
  companyType,
  docList,
  emptyMessage = 'Documentos pendentes de envio',
  docLoading,
  handleReplace,
  handleApprove,
  handleApproveWithReservation,
  handleReject,
  handleCancel,
  disabled,
  hasOpenChangeRequest = false,
  changeRequestDocIds,
  hideTitle = false,
}: DocumentsTableProps): JSX.Element {
  const [openRejectionDialog, setOpenRejectionDialog] = useState<
    NoticeDocumentsData | undefined
  >(undefined);
  const [openHistoryDialog, setOpenHistoryDialog] = useState<
    NoticeDocumentsData | undefined
  >(undefined);

  const { handleDownloadFile } = useGeneral();

  // `hasOpenChangeRequest` releases the whole table; `changeRequestDocIds`
  // narrows the release to the documents the pending request brought, so the
  // rest of an approved member's history stays read-only.
  const isChangeRequestDisabled = (doc: NoticeDocumentsData): boolean =>
    hasOpenChangeRequest || changeRequestDocIds?.has(doc.id) ? false : disabled;

  const handleStatus = (doc: NoticeDocumentsData): JSX.Element => {
    const docName = getDocumentRowName(doc);
    const changeRequestDisabled = isChangeRequestDisabled(doc);
    switch (doc.status) {
      case DocumentStatus.PENDING:
        return (
          <CancelDocumentDialog
            docId={doc.id}
            name={docName}
            handleCancel={handleCancel}
            disabled={disabled}
          />
        );

      case DocumentStatus.UNDER_ANALYSIS:
        return (
          <>
            <RejectDialog
              docId={doc.id}
              name={docName}
              document={doc.file}
              docLoading={docLoading}
              handleReject={handleReject}
              disabled={changeRequestDisabled}
            />
            <ApproveMenu
              docId={doc.id}
              name={docName}
              document={doc.file}
              docLoading={docLoading}
              disabled={changeRequestDisabled}
              handleApprove={handleApprove}
              handleApproveWithReservation={handleApproveWithReservation}
            />
          </>
        );

      case DocumentStatus.APPROVED:
      case DocumentStatus.APPROVED_WITH_RESERVATIONS:
        return (
          <RejectDialog
            docId={doc.id}
            name={docName}
            document={doc.file}
            docLoading={docLoading}
            handleReject={handleReject}
            disapprove
            disabled={disabled}
          />
        );

      case DocumentStatus.REJECTED:
        return (
          <OutlinedButton
            sx={{ maxWidth: '260px' }}
            onClick={() => handleReplace(doc.id)}
            disabled={changeRequestDisabled}
          >
            solicitar substituição
          </OutlinedButton>
        );

      case DocumentStatus.CANCELED:
        return (
          <OutlinedButton sx={{ maxWidth: '260px' }} disabled>
            cancelar
          </OutlinedButton>
        );

      case DocumentStatus.REPLACEMENT_REQUEST:
        return (
          <OutlinedButton sx={{ maxWidth: '260px' }} disabled>
            solicitar substituição
          </OutlinedButton>
        );
      default:
        return <Box />;
    }
  };

  const closeRejectionDialog = (): void => setOpenRejectionDialog(undefined);

  const closeHistoryDialog = (): void => setOpenHistoryDialog(undefined);

  const displayName =
    name === Constants.legalAnalysisSection && companyType
      ? `${name} - ${convertNoticeCompanyType(companyType)}`
      : name;

  return (
    <Box my={hideTitle ? 0 : 5}>
      {!hideTitle && (
        <Typography color="common.grey" fontSize={21} fontWeight={600} my={3}>
          {displayName}
        </Typography>
      )}
      <TableContainer component={Paper}>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCellHeader sx={{ padding: '20px 10px', width: '3%' }}>
                {IconAttachFileMS}
              </TableCellHeader>
              <TableCellHeader width="17%">documento</TableCellHeader>
              <TableCellHeader align="center" width="13%">
                data de entrada
              </TableCellHeader>
              <TableCellHeader align="center" width="17%">
                status
              </TableCellHeader>
              <TableCellHeader align="center" width="20%">
                motivo
              </TableCellHeader>
              <TableCellHeader align="center" width="30%">
                ações
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {docList.length === 0 && (
              <StyledRow>
                <StyledCell align="center" colSpan={6}>
                  {emptyMessage}
                </StyledCell>
              </StyledRow>
            )}
            {docList.map((item) => {
              const statusStyle = getDocumentRowStatusStyle(item);
              const rowName = getDocumentRowName(item);

              if (isMissingRequirement(item)) {
                return (
                  <StyledRow key={getDocumentRowKey(item)}>
                    <StyledCell sx={{ padding: '20px 10px' }}>
                      <DownloadButton disabled>
                        {IconDescriptionMS}
                      </DownloadButton>
                    </StyledCell>
                    <StyledCell>
                      <StyledTooltip title={rowName} placement="top">
                        <Box>
                          <DocumentNameButton disabled>
                            <span>{rowName}</span>
                          </DocumentNameButton>
                        </Box>
                      </StyledTooltip>
                    </StyledCell>
                    <StyledCell align="center">—</StyledCell>
                    <StatusCell align="center">
                      <StatusChip
                        disabled
                        sx={{
                          color: statusStyle.color,
                          backgroundColor: statusStyle.background,
                        }}
                      >
                        {statusStyle.icon}
                        <span>{getDocumentRowStatusLabel(item)}</span>
                      </StatusChip>
                    </StatusCell>
                    <StyledCell align="center" />
                    <StyledCell />
                  </StyledRow>
                );
              }

              return (
                <StyledRow key={getDocumentRowKey(item)}>
                  <StyledCell sx={{ padding: '20px 10px' }}>
                    <DownloadButton
                      disabled={!item.url}
                      onClick={() => handleDownloadFile(item.url, item.file)}
                    >
                      {IconDescriptionMS}
                    </DownloadButton>
                  </StyledCell>
                  <StyledCell>
                    <StyledTooltip title={rowName} placement="top">
                      <Box>
                        <DocumentNameButton
                          disabled={!item.url}
                          onClick={() =>
                            handleDownloadFile(item.url, item.file)
                          }
                        >
                          <span>{rowName}</span>
                        </DocumentNameButton>
                      </Box>
                    </StyledTooltip>
                  </StyledCell>
                  <StyledCell align="center">
                    {formatDate(item.sent_at)}
                  </StyledCell>
                  <StatusCell align="center">
                    <StatusChip
                      sx={{
                        color: statusStyle.color,
                        backgroundColor: statusStyle.background,
                      }}
                      onClick={() => setOpenHistoryDialog(item)}
                    >
                      {statusStyle.icon}
                      <span>{getDocumentRowStatusLabel(item)}</span>
                      <StatusChipChevron>
                        {IconArrowForwardIosMS}
                      </StatusChipChevron>
                    </StatusChip>
                  </StatusCell>
                  <StyledCell align="center">
                    <ActionButton
                      sx={{ textAlign: 'center' }}
                      onClick={() => setOpenRejectionDialog(item)}
                    >
                      <span>
                        {stripHtml(
                          item.rejection_reason || item.reservation_note
                        )}
                      </span>
                    </ActionButton>
                  </StyledCell>
                  <StyledCell>
                    <ButtonsWrapper>{handleStatus(item)}</ButtonsWrapper>
                  </StyledCell>
                </StyledRow>
              );
            })}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <RejectReasonDialog
        open={!!openRejectionDialog}
        closeDialog={closeRejectionDialog}
        title={
          !openRejectionDialog?.rejection_reason &&
          openRejectionDialog?.reservation_note
            ? 'Ressalva'
            : 'Motivo'
        }
        rejectionReason={
          openRejectionDialog?.rejection_reason ||
          openRejectionDialog?.reservation_note ||
          ''
        }
        document={
          openRejectionDialog ? getDocumentRowName(openRejectionDialog) : ''
        }
      />
      <DocumentHistoryDialog
        open={!!openHistoryDialog}
        closeDialog={closeHistoryDialog}
        document={openHistoryDialog}
      />
    </Box>
  );
}
